import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Award, Clock, CheckCircle, Star, BookOpen, Target, Shield,
  ArrowRight, Users, Calendar, Plus, Trash2, X
} from 'lucide-react';
import { toast } from 'sonner';
import { PageEditProvider, usePageEditMode } from '@/context/PageEditModeContext';
import { PageEditable } from '@/components/edit/PageEditable';
import { PageEditToolbar } from '@/components/edit/PageEditToolbar';
import { adminFetch } from '@/utils/adminAuth';


const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Certification Card Component  
const CertificationCard = ({ cert, onEnroll, onDelete }) => {
  const { enabled } = usePageEditMode();
  const requirements = cert.requirements || [];
  const benefits = cert.benefits || [];
  const Icon = cert.icon || Award;
  const p = `cert.${cert.id}`;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className={`bg-gradient-to-br ${cert.gradient || 'from-primary/10 to-accent/10'} border-2 ${cert.borderColor || 'border-primary/30'} rounded-3xl p-6 relative overflow-hidden`}
    >
      <div className="absolute top-0 right-0 w-32 h-32 opacity-10">
        <Award className="w-full h-full" />
      </div>

      {enabled && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDelete(cert); }}
          data-edit-safe
          data-testid={`delete-cert-${cert.id}`}
          className="absolute top-3 right-3 z-20 p-2 rounded-full bg-red-500/90 hover:bg-red-500 text-white shadow-lg"
          title="Delete this certificate"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      )}

      <div className="relative z-10">
        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${cert.badgeColor || 'bg-primary/20 text-primary'} mb-4`}>
          <Icon className="w-5 h-5" />
          <span className="font-bold">
            <PageEditable path={`${p}.level`} as="span" fallback={cert.level || 'Certificate'} />
          </span>
        </div>

        <h3 className="text-2xl font-bold font-display text-foreground mb-2">
          <PageEditable path={`${p}.title`} as="span" fallback={cert.title || ''} />
        </h3>
        <p className="text-muted-foreground mb-4">
          <PageEditable path={`${p}.description`} as="span" multiline fallback={cert.description || ''} />
        </p>

        <div className="flex items-center gap-2 mb-4 text-sm text-muted-foreground">
          <Clock className="w-4 h-4 text-primary" />
          <PageEditable path={`${p}.duration`} as="span" fallback={cert.duration || 'Self-paced program'} />
        </div>

        {requirements.length > 0 && (
          <div className="space-y-2 mb-6">
            <h4 className="font-bold text-foreground text-sm">
              <PageEditable path={`${p}.requirementsLabel`} as="span" fallback="Requirements:" />
            </h4>
            {requirements.map((req, idx) => (
              <div key={idx} className="flex items-center gap-2 text-sm text-muted-foreground">
                <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />
                <PageEditable path={`${p}.requirements.${idx}`} as="span" multiline fallback={req} />
              </div>
            ))}
          </div>
        )}

        {benefits.length > 0 && (
          <div className="space-y-2 mb-6">
            <h4 className="font-bold text-foreground text-sm">
              <PageEditable path={`${p}.benefitsLabel`} as="span" fallback="You'll Receive:" />
            </h4>
            {benefits.map((benefit, idx) => (
              <div key={idx} className="flex items-center gap-2 text-sm text-muted-foreground">
                <Star className="w-4 h-4 text-primary flex-shrink-0" />
                <PageEditable path={`${p}.benefits.${idx}`} as="span" multiline fallback={benefit} />
              </div>
            ))}
          </div>
        )}

        <div className="pt-4 border-t border-white/10">
          <div className="flex items-center justify-between mb-4">
            <div>
              <span className="text-3xl font-bold text-foreground">
                $<PageEditable path={`${p}.price`} as="span" fallback={String(cert.price || 0)} />
              </span>
              <span className="text-sm text-muted-foreground ml-2">
                <PageEditable path={`${p}.priceSuffix`} as="span" fallback="one-time" />
              </span>
            </div>
          </div>
          <button
            onClick={() => onEnroll(cert)}
            className={`w-full flex items-center justify-center gap-2 px-6 py-4 ${cert.buttonColor || 'bg-primary hover:bg-primary/90 text-black'} rounded-full font-bold transition-all hover:scale-105 shadow-glow`}
          >
            <Award className="w-5 h-5" />
            <PageEditable path={`${p}.cta`} as="span" fallback="Enroll in Certification" />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

const CertificatesInner = () => {
  const { enabled } = usePageEditMode();
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCert, setSelectedCert] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newPrice, setNewPrice] = useState('99');
  const [creating, setCreating] = useState(false);

  const fetchCertificates = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/courses/list/all`);
      const data = await response.json();
      
      const certItems = data
        .filter(item => item.itemType === 'certificate')
        .map((cert, index) => ({
          ...cert,
          icon: index === 0 ? BookOpen : index === 1 ? Target : Shield,
          gradient: index === 0 ? 'from-green-500/10 to-emerald-500/10' : 
                    index === 1 ? 'from-blue-500/10 to-cyan-500/10' : 
                    index === 2 ? 'from-primary/10 to-accent/10' :
                    index === 3 ? 'from-purple-500/10 to-pink-500/10' : 'from-amber-500/10 to-orange-500/10',
          borderColor: index === 0 ? 'border-green-500/30' : 
                       index === 1 ? 'border-blue-500/30' : 
                       index === 2 ? 'border-primary/30' :
                       index === 3 ? 'border-purple-500/30' : 'border-amber-500/30',
          badgeColor: index === 0 ? 'bg-green-500/20 text-green-400' : 
                      index === 1 ? 'bg-blue-500/20 text-blue-400' : 
                      index === 2 ? 'bg-primary/20 text-primary' :
                      index === 3 ? 'bg-purple-500/20 text-purple-400' : 'bg-amber-500/20 text-amber-400',
          buttonColor: index === 0 ? 'bg-green-500 hover:bg-green-600 text-white' : 
                       index === 1 ? 'bg-blue-500 hover:bg-blue-600 text-white' : 
                       index === 2 ? 'bg-primary hover:bg-primary/90 text-black' :
                       index === 3 ? 'bg-purple-500 hover:bg-purple-600 text-white' : 'bg-amber-500 hover:bg-amber-600 text-black',
          level: cert.level ? `Level ${cert.level}` : `Level ${index + 1}`,
        }));
      
      setCertificates(certItems);
    } catch (error) {
      console.error('Error fetching certificates:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCertificates();
  }, []);

  const handleCreate = async () => {
    const title = newTitle.trim();
    if (!title) { toast.error('Enter a certificate title'); return; }
    setCreating(true);
    try {
      const price = parseFloat(newPrice) || 0;
      const params = new URLSearchParams({ title, course_type: 'certificate' });
      const res = await adminFetch(`/admin/courses/create?${params.toString()}`, { method: 'POST' });
      if (!res.ok) throw new Error('Create failed');
      const data = await res.json();
      // Update the price to what user set (default template uses $99)
      if (price !== 99) {
        await adminFetch(`/admin/courses/${data.course_id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ price })
        });
      }
      toast.success('Certificate created');
      setShowAddModal(false);
      setNewTitle('');
      setNewPrice('99');
      await fetchCertificates();
    } catch (e) {
      toast.error(e.message || 'Failed to create certificate');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (cert) => {
    if (!window.confirm(`Delete "${cert.title}"? This cannot be undone.`)) return;
    try {
      const res = await adminFetch(`/admin/courses/${cert.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Delete failed');
      toast.success('Certificate deleted');
      await fetchCertificates();
    } catch (e) {
      toast.error(e.message || 'Failed to delete certificate');
    }
  };

  const handleEnroll = (cert) => {
    setSelectedCert(cert);
    setShowModal(true);
  };

  const handleCheckout = async () => {
    if (!selectedCert) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/courses/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          course_id: selectedCert.id,
          course_name: `${selectedCert.title} Certification`,
          price: selectedCert.price
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.checkout_url) {
          window.location.href = data.checkout_url;
        }
      } else {
        toast.error('Failed to start checkout');
      }
    } catch (err) {
      toast.error('Checkout error. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading certificates...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent" />
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/30 mb-6">
              <Award className="w-5 h-5 text-primary" />
              <span className="text-primary font-medium">
                <PageEditable path="hero.eyebrow" fallback="Escola de Samba Casa Samba Certifications" />
              </span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-display mb-6">
              <PageEditable path="hero.titlePre" fallback="Become a " />
              <span className="text-primary"><PageEditable path="hero.titleAccent" fallback="Certified" /></span>
              <PageEditable path="hero.titlePost" fallback=" Practitioner" />
            </h1>
            
            <PageEditable
              path="hero.subtitle"
              as="p"
              multiline
              className="text-lg text-muted-foreground mb-8"
              fallback="Earn official Escola de Samba Casa Samba certifications and join a global community of Afro-Brasilian arts practitioners and instructors."
            />

            <div className="flex flex-wrap justify-center gap-8 text-center">
              <div>
                <p className="text-3xl font-bold text-primary">{certificates.length}</p>
                <p className="text-sm text-muted-foreground">
                  <PageEditable path="stats.programsLabel" fallback="Certification Programs" />
                </p>
              </div>
              <div>
                <p className="text-3xl font-bold text-primary">
                  <PageEditable path="stats.yearsNumber" fallback="38+" />
                </p>
                <p className="text-sm text-muted-foreground">
                  <PageEditable path="stats.yearsLabel" fallback="Years of Tradition" />
                </p>
              </div>
              <div>
                <p className="text-3xl font-bold text-primary">
                  <PageEditable path="stats.recognitionNumber" fallback="Global" />
                </p>
                <p className="text-sm text-muted-foreground">
                  <PageEditable path="stats.recognitionLabel" fallback="Recognition" />
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Certifications Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold font-display mb-4">
              <PageEditable path="programs.heading" fallback="Certification Programs" />
            </h2>
            <PageEditable
              path="programs.subtitle"
              as="p"
              multiline
              className="text-muted-foreground max-w-2xl mx-auto"
              fallback="Choose your path from student to certified instructor"
            />
          </div>

          {certificates.length === 0 && !enabled ? (
            <div className="text-center py-12">
              <Award className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No certification programs available at the moment.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {certificates.map((cert) => (
                <CertificationCard 
                  key={cert.id} 
                  cert={cert} 
                  onEnroll={handleEnroll}
                  onDelete={handleDelete}
                />
              ))}
              {enabled && (
                <button
                  type="button"
                  onClick={() => setShowAddModal(true)}
                  data-edit-safe
                  data-testid="add-certificate-btn"
                  className="min-h-[420px] flex flex-col items-center justify-center gap-3 rounded-3xl border-2 border-dashed border-primary/50 bg-primary/5 hover:bg-primary/10 hover:border-primary transition-all text-primary font-bold"
                >
                  <Plus className="w-12 h-12" />
                  <span className="text-lg">Add New Certificate</span>
                  <span className="text-xs text-muted-foreground font-normal">Creates a fresh certificate program</span>
                </button>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Why Get Certified */}
      <section className="py-16 bg-card/50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold font-display text-center mb-12">
            <PageEditable path="whyCertified.headingPre" fallback="Why Get " />
            <span className="text-primary"><PageEditable path="whyCertified.headingAccent" fallback="Certified" /></span>
            <PageEditable path="whyCertified.headingPost" fallback="?" />
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl font-bold mb-2">
                <PageEditable path="whyCertified.official.title" fallback="Official Recognition" />
              </h3>
              <PageEditable
                path="whyCertified.official.body"
                as="p"
                multiline
                className="text-muted-foreground"
                fallback="Earn credentials recognized by the global Afro-Brasilian arts community."
              />
            </div>
            
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl font-bold mb-2">
                <PageEditable path="whyCertified.community.title" fallback="Join the Community" />
              </h3>
              <PageEditable
                path="whyCertified.community.body"
                as="p"
                multiline
                className="text-muted-foreground"
                fallback="Connect with practitioners and instructors worldwide."
              />
            </div>
            
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Calendar className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl font-bold mb-2">
                <PageEditable path="whyCertified.teaching.title" fallback="Teaching Opportunities" />
              </h3>
              <PageEditable
                path="whyCertified.teaching.body"
                as="p"
                multiline
                className="text-muted-foreground"
                fallback="Certified instructors can teach under the Escola de Samba Casa Samba name."
              />
            </div>
          </div>
        </div>
      </section>

      {/* Enrollment Modal */}
      {showModal && selectedCert && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-card border border-border rounded-2xl p-6 max-w-md w-full"
          >
            <h3 className="text-xl font-bold mb-2">
              <PageEditable path="enrollModal.heading" as="span" fallback="Enroll in Certification" />
            </h3>
            <p className="text-muted-foreground mb-4">{selectedCert.title}</p>
            
            <div className="bg-background rounded-xl p-4 mb-6">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">
                  <PageEditable path="enrollModal.feeLabel" as="span" fallback="Certification Fee" />
                </span>
                <span className="text-2xl font-bold">${selectedCert.price}</span>
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 py-3 bg-muted hover:bg-muted/80 rounded-xl font-medium"
              >
                <PageEditable path="enrollModal.cancel" as="span" fallback="Cancel" />
              </button>
              <button
                onClick={handleCheckout}
                className="flex-1 py-3 bg-primary hover:bg-primary/90 text-black rounded-xl font-bold flex items-center justify-center gap-2"
              >
                <ArrowRight className="w-4 h-4" />
                <PageEditable path="enrollModal.checkout" as="span" fallback="Proceed to Checkout" />
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Add Certificate Modal */}
      {showAddModal && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70"
          onClick={() => !creating && setShowAddModal(false)}
          data-edit-safe
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-card border border-border rounded-2xl p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
            data-edit-safe
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold">Add New Certificate</h3>
              <button
                type="button"
                onClick={() => !creating && setShowAddModal(false)}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Title *</label>
                <input
                  type="text"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  placeholder="e.g. Level 4 Master Certification"
                  data-testid="new-cert-title-input"
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Price (USD)</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={newPrice}
                  onChange={(e) => setNewPrice(e.target.value)}
                  data-testid="new-cert-price-input"
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                A blank certificate template will be created. You can edit every field inline on the card afterwards.
              </p>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                type="button"
                onClick={() => !creating && setShowAddModal(false)}
                className="flex-1 py-3 bg-muted hover:bg-muted/80 rounded-xl font-medium"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreate}
                disabled={creating || !newTitle.trim()}
                data-testid="create-cert-btn"
                className="flex-1 py-3 bg-primary hover:bg-primary/90 text-black rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <Plus className="w-4 h-4" />
                {creating ? 'Creating…' : 'Create Certificate'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};


const Certificates = () => (
  <PageEditProvider slug="certificates">
    <CertificatesInner />
    <PageEditToolbar />
  </PageEditProvider>
);

export { Certificates };
export default Certificates;
